import { ContactNotificationsOptions, ScheduleConfig, ScheduleUpdateParams } from '../../types/shared-location.types';
import { computed, observable, action, makeObservable } from 'mobx';
import { CustomerExperienceSchedulingData, DeliveryWindow } from '@bringg/types';
import { get as _get, isNull as _isNull, omit as _omit } from 'lodash';
import BaseCustomerExperienceStoreType from '../shared-location/base-customer-experience-store-type';
import { getEnv, getRoot } from 'mobx-easy';
import RootStore from '../root-store';
import { RootEnv } from '../../helpers/create-store';

export type DeliveryWindowWithId = DeliveryWindow & { id: number };

class ScheduleStore extends BaseCustomerExperienceStoreType<CustomerExperienceSchedulingData> {
	deliveryWindow: DeliveryWindowWithId = null;

	isScheduled: boolean = false;

	approveTermsAndConditions: boolean = false;

	private _contactNotifications: ContactNotificationsOptions = null;

	constructor() {
		super();

		makeObservable<ScheduleStore, '_contactNotifications'>(this, {
			deliveryWindow: observable,
			isScheduled: observable,
			approveTermsAndConditions: observable,
			_contactNotifications: observable,
			setDeliveryWindow: action,
			setIsScheduled: action,
			setContactNotifications: action,
			setApproveTermsAndConditions: action,
			contactNotifications: computed,
			enableContactNotificationEdit: computed,
			deliveryWindows: computed,
			couldSubmitContactNotifications: computed
		});
	}

	setDeliveryWindow = (deliveryWindow: DeliveryWindowWithId) => {
		this.deliveryWindow = deliveryWindow;
	};

	setIsScheduled = (isScheduled: boolean) => {
		this.isScheduled = isScheduled;
	};

	setApproveTermsAndConditions = (approveTermsAndConditions: boolean) => {
		this.approveTermsAndConditions = approveTermsAndConditions;
	};

	setContactNotifications = (contactNotifications: ContactNotificationsOptions) => {
		this._contactNotifications = contactNotifications;
	};

	get contactNotifications(): ContactNotificationsOptions {
		if (_isNull(this._contactNotifications)) {
			return {
				allow_send_email: _get(this.customerExperience, 'opt_in_out_data.allow_send_email'),
				allow_send_sms: _get(this.customerExperience, 'opt_in_out_data.allow_send_sms')
			};
		}

		return this._contactNotifications;
	}

	get enableContactNotificationEdit(): boolean {
		return Boolean(_get(this.customerExperience, 'opt_in_out_data', false));
	}

	get deliveryWindows(): DeliveryWindowWithId[] {
		return _get(this.customerExperience, 'scheduling.delivery_windows', []) as DeliveryWindowWithId[];
	}

	get couldSubmitContactNotifications(): boolean {
		const anyNotificationMethodEnabled = Object.values(this.contactNotifications).includes(true);

		const couldSubmitContactNotifications =
			!anyNotificationMethodEnabled ||
			!_get(this.customerExperience, 'opt_in_out_data.terms_and_conditions_text') ||
			this.approveTermsAndConditions ||
			_get(this.customerExperience, 'isUserAuthenticated');

		return couldSubmitContactNotifications;
	}

	schedule = async () => {
		const { id, start_time, end_time } = this.deliveryWindow;
		const { shared_location_uuid } = this.customerExperience;
		let scheduleUpdateParams: ScheduleUpdateParams = {
			delivery_window_id: id,
			start_time: start_time,
			end_time: end_time
		};

		const { sharedLocationService } = getEnv<RootEnv>();

		try {
			if (this.enableContactNotificationEdit) {
				await sharedLocationService.postOptInOutContactNotifications(
					this.contactNotifications,
					shared_location_uuid
				);
			}

			await sharedLocationService.schedule(scheduleUpdateParams, shared_location_uuid);
			this.setIsScheduled(true);
		} catch (e) {
			console.error(e);
			getRoot<RootStore>().setIsError(true);
		}
	};

	updateContactNotifications = async () => {
		const { shared_location_uuid } = this.customerExperience;

		const { sharedLocationService } = getEnv<RootEnv>();

		try {
			await sharedLocationService.postOptInOutContactNotifications(
				this.contactNotifications,
				shared_location_uuid
			);
		} catch (e) {
			console.error(e);
			getRoot<RootStore>().setIsError(true);
		}
	};

	serializeToV2(oldSharedLocationConfig: ScheduleConfig) {
		const { reschedule } = oldSharedLocationConfig;

		return {
			..._omit(oldSharedLocationConfig, 'reschedule'),
			scheduling: reschedule
		};
	}

	initialize() {}
	dispose() {}
}

export default ScheduleStore;
